import { useState, useEffect } from 'react';
import { flowsApi } from '../services/api';
import { Flow } from '@dynamic-flow/types';
import { cacheFormTheme } from '../utils/formTheme';

// API Response type with data wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Union type for both response formats
type FlowResponse = ApiResponse<Flow> | Flow;

export type FlowLoadType = 'draft' | 'published';

interface UseFormFlowResult {
  flow: Flow | null;
  loading: boolean;
  error:
    | string
    | {
        message: string;
        details?: string;
        reason?: string;
        metadata?: any;
      }
    | null;
  reload: (lang: string) => void;
}

export const useFormFlow = (
  flowId: string | undefined,
  loadType: FlowLoadType,
  lang?: string
): UseFormFlowResult => {
  const [flow, setFlow] = useState<Flow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<
    | string
    | {
        message: string;
        details?: string;
        reason?: string;
        metadata?: any;
      }
    | null
  >(null);

  const loadFlow = async (lang: string = 'en') => {
    if (!flowId) {
      setError('Flow ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load flow data based on type
      const response: FlowResponse =
        loadType === 'draft'
          ? await flowsApi.getDraft(flowId, lang)
          : await flowsApi.getPublished(flowId, lang);

      // Handle API response with data wrapper using type guard
      const flowData: Flow = response.data;
      setFlow(flowData);
      cacheFormTheme(flowId, flowData.theme);
    } catch (err) {
      console.error(`Error loading ${loadType} flow:`, err);

      // Check if error response contains structured error data
      if (err && typeof err === 'object' && 'error' in err) {
        // This is likely an API error response with structured data
        const apiError = err as any;
        setError({
          message: apiError.error || 'An error occurred',
          details: apiError.details,
          reason: apiError.reason,
          metadata: apiError.metadata,
        });
      } else if (err instanceof Error) {
        // Handle standard Error objects
        if (err.message.includes('404') || err.message.includes('not found')) {
          if (loadType === 'draft') {
            setError(
              'Flow not found. Make sure the flow exists and has been saved.'
            );
          } else {
            setError(
              'This form is not currently available or not published. Please make sure the form has been published and the flow ID is correct.'
            );
          }
        } else if (
          err.message.includes('Failed to fetch') ||
          err.message.includes('NetworkError')
        ) {
          setError(
            'Cannot connect to API server. Please make sure the API server is running.'
          );
        } else if (err.message.includes('500')) {
          setError('Server error. Please try again later.');
        } else {
          setError(`Failed to load form: ${err.message}`);
        }
      } else {
        setError(`Failed to load ${loadType} form`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlow(lang);
  }, [flowId, loadType, lang]);

  const reload = (lang: string) => {
    loadFlow(lang);
  };

  return { flow, loading, error, reload };
};
